/**
 * Korrekturen — der Prüf-Eingang als eigene Leitstand-Seite (Phase B).
 *
 * Setzt Festlegung 6+7 aus OBERFLAECHE-ENTSCHEIDUNGEN.md um: Jede Karte zeigt
 * Inhalt (Aus … wird …), Grund und Alter; die Prüfung ist eine aufklappende
 * Vorher-→-Nachher-Karte, jede Zeile einzeln abwählbar, Komplett-Ablehnung
 * verlangt einen Satz. Altformat-Anträge ohne Änderungszeilen sind „ohne
 * Wirkung" schließbar. Datenquelle und Wege sind dieselben wie im bewährten
 * Dashboard-Eingang (K1/K2) — hier nur die Leitstand-Heimat dafür.
 */

import { useCallback, useEffect, useState } from 'react';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Kennzahl, Notiz } from '../components/leitstand';
import { resolveCorrectionRequestV2, updateCorrectionRequestStatus } from '../api/client';

interface KorrekturItem {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  oldValueLabel?: string;
  newValueLabel?: string;
}

interface OffeneKorrektur {
  id: string;
  targetType: string;
  reasonText: string;
  workerComment: string | null;
  correctionItems: KorrekturItem[] | null;
  createdAt: string;
  entryContext: {
    lineName?: string;
    subLineName?: string;
    machineName?: string;
    customerName?: string;
    customerGroupName?: string;
    weightKg?: number;
    recordedByName?: string;
  };
}

const FELD_LABELS: Record<string, string> = {
  lineId: 'Strecke',
  subLineId: 'Substrecke',
  customerId: 'Kunde',
  customerGroupId: 'Kundengruppe',
  weightKg: 'Gewicht',
  machineId: 'Maschine',
  programId: 'Programm',
  laundryClassId: 'Wäscheklasse',
  articleGroupId: 'Artikelgruppe',
};

function feldLabel(field: string): string {
  return FELD_LABELS[field] ?? field;
}

function wert(i: KorrekturItem, seite: 'alt' | 'neu'): string {
  const label = seite === 'alt' ? i.oldValueLabel : i.newValueLabel;
  const roh = seite === 'alt' ? i.oldValue : i.newValue;
  return label ?? (roh === null || roh === undefined ? '—' : String(roh));
}

function alterLabel(createdAt: string): { text: string; alt: boolean } {
  const d = new Date(createdAt);
  const tage = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (tage <= 0) {
    return {
      text: `heute ${d.toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })}`,
      alt: false,
    };
  }
  if (tage === 1) return { text: 'gestern', alt: false };
  return {
    text: `seit ${d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' })} · ${tage} Tage`,
    alt: tage >= 7,
  };
}

export default function CorrectionsLeitstand() {
  const { user } = useAuth();

  const [offene, setOffene] = useState<OffeneKorrektur[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  // Prüfzustand je aufgeklappter Karte
  const [offeneId, setOffeneId] = useState<string | null>(null);
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());
  const [kommentar, setKommentar] = useState('');
  const [sende, setSende] = useState(false);
  const [aktionsFehler, setAktionsFehler] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/dashboard/data-quality', { credentials: 'include' });
      if (!res.ok) throw new Error('Der Eingang konnte nicht geladen werden');
      const body = await res.json();
      setOffene(Array.isArray(body?.openCorrections) ? body.openCorrections : []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Eingang konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  const klappeAuf = (id: string) => {
    setOffeneId(id === offeneId ? null : id);
    setAbgewaehlt(new Set());
    setKommentar('');
    setAktionsFehler('');
  };

  const entferneAusListe = (id: string) => {
    setOffene((prev) => prev.filter((k) => k.id !== id));
    setOffeneId(null);
  };

  const entscheide = async (k: OffeneKorrektur) => {
    const items = k.correctionItems ?? [];
    const angenommen = items.filter((i) => !abgewaehlt.has(i.field));
    const alleAbgelehnt = angenommen.length === 0;
    if (alleAbgelehnt && kommentar.trim().length < 5) {
      setAktionsFehler('Für eine Ablehnung bitte kurz begründen (mindestens 5 Zeichen).');
      return;
    }
    setSende(true);
    setAktionsFehler('');
    try {
      await resolveCorrectionRequestV2(k.id, {
        itemResolutions: items.map((i) => ({
          field: i.field,
          resolution: abgewaehlt.has(i.field) ? 'REJECTED' : 'ACCEPTED',
        })),
        adminComment: kommentar.trim() || undefined,
      });
      entferneAusListe(k.id);
    } catch (e) {
      setAktionsFehler(
        e instanceof Error ? e.message : 'Die Entscheidung konnte nicht gespeichert werden',
      );
    } finally {
      setSende(false);
    }
  };

  const schliesseAltformat = async (k: OffeneKorrektur) => {
    setSende(true);
    setAktionsFehler('');
    try {
      await updateCorrectionRequestStatus(
        k.id,
        'REJECTED',
        'Altformat ohne Änderungszeilen — ohne Wirkung geschlossen',
      );
      entferneAusListe(k.id);
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Konnte nicht geschlossen werden');
    } finally {
      setSende(false);
    }
  };

  const aelteste = offene.reduce<number>((max, k) => {
    const tage = Math.floor((Date.now() - new Date(k.createdAt).getTime()) / 86400000);
    return Math.max(max, tage);
  }, 0);

  return (
    <LeitstandRahmen titel="Korrekturen" benutzer={user?.displayName}>
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !offene.length && !fehler && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Eingang …</p>
      )}

      {!laden && !fehler && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Offene Anträge"
              wert={String(offene.length)}
              tendenz={offene.length > 0 ? 'runter' : 'neutral'}
              neben={offene.length === 0 ? 'alles erledigt' : undefined}
            />
            <Kennzahl
              marke="Ältester Antrag"
              klein
              wert={offene.length === 0 ? '—' : aelteste === 0 ? 'heute' : `${aelteste} Tage`}
              tendenz={aelteste >= 7 ? 'runter' : 'neutral'}
            />
            <Kennzahl
              marke="Stand"
              klein
              wert={getTodayBerlin(new Date()).split('-').reverse().join('.')}
            />
          </div>

          {offene.length === 0 ? (
            <Karte titel="Eingang" marke="Korrekturanträge der Stationen">
              <p className="py-6 text-center text-sm text-tinte-3">
                Keine offenen Korrekturanträge — die Stationen haben nichts gemeldet, was auf eine
                Entscheidung wartet.
              </p>
            </Karte>
          ) : (
            offene.map((k) => {
              const items = k.correctionItems ?? [];
              const istAltformat = items.length === 0;
              const alter = alterLabel(k.createdAt);
              const kontext = k.entryContext.machineName ?? k.entryContext.lineName ?? '—';
              const wer = k.entryContext.customerName ?? k.entryContext.customerGroupName;
              const grund = k.workerComment || k.reasonText || '—';
              const aufgeklappt = offeneId === k.id;
              const angenommen = items.filter((i) => !abgewaehlt.has(i.field)).length;

              return (
                <Karte
                  key={k.id}
                  titel={`${k.targetType === 'PROCESSING_ENTRY' ? 'Vorarbeiter' : 'WSA'} · ${kontext}${wer ? ` · ${wer}` : ''}`}
                  marke={alter.alt ? `⚠ ${alter.text}` : alter.text}
                >
                  <div className="flex flex-col gap-3">
                    {istAltformat ? (
                      <p className="text-sm text-tinte-2">
                        Antrag im Altformat — enthält keine prüfbaren Änderungszeilen.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {items.map((i) => {
                          const weg = aufgeklappt && abgewaehlt.has(i.field);
                          return (
                            <div
                              key={i.field}
                              className={`flex flex-wrap items-baseline gap-2 text-sm ${weg ? 'opacity-45 line-through' : ''}`}
                            >
                              {aufgeklappt && (
                                <input
                                  type="checkbox"
                                  checked={!abgewaehlt.has(i.field)}
                                  onChange={() => {
                                    const neu = new Set(abgewaehlt);
                                    if (neu.has(i.field)) neu.delete(i.field);
                                    else neu.add(i.field);
                                    setAbgewaehlt(neu);
                                  }}
                                  className="mr-1 h-4 w-4 accent-[var(--akzent,#0D6068)]"
                                />
                              )}
                              <Kennung>{feldLabel(i.field)}</Kennung>
                              <span className="text-tinte-3">{wert(i, 'alt')}</span>
                              <span className="text-tinte-3">→</span>
                              <span className="font-zahl font-medium">{wert(i, 'neu')}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p className="text-[13px] text-tinte-2">
                      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        Grund{' '}
                      </span>
                      {grund}
                      {k.entryContext.recordedByName && (
                        <span className="text-tinte-3">
                          {' '}
                          · erfasst von {k.entryContext.recordedByName}
                        </span>
                      )}
                    </p>

                    {aufgeklappt && (
                      <textarea
                        value={kommentar}
                        onChange={(e) => setKommentar(e.target.value)}
                        placeholder={
                          angenommen === 0
                            ? 'Begründung der Ablehnung (Pflicht)…'
                            : 'Anmerkung (optional)…'
                        }
                        rows={2}
                        className="w-full rounded-[4px] border border-linie-2 bg-flaeche px-3 py-2 text-sm text-tinte"
                      />
                    )}

                    {aktionsFehler && aufgeklappt && (
                      <p className="text-[13px] text-stoer">{aktionsFehler}</p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {istAltformat ? (
                        <button
                          onClick={() => void schliesseAltformat(k)}
                          disabled={sende}
                          className="rounded-[4px] border border-linie-stark bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2 hover:border-linie-stark disabled:opacity-50"
                        >
                          Ohne Wirkung schließen
                        </button>
                      ) : !aufgeklappt ? (
                        <button
                          onClick={() => klappeAuf(k.id)}
                          className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
                        >
                          Prüfen
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => void entscheide(k)}
                            disabled={sende}
                            className={`rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50 ${
                              angenommen === 0
                                ? 'border border-stoer bg-stoer text-akzent-satz'
                                : 'border border-akzent bg-akzent text-akzent-satz'
                            }`}
                          >
                            {angenommen === 0
                              ? 'Alles ablehnen'
                              : angenommen === items.length
                                ? `${angenommen === 1 ? 'Änderung' : `${angenommen} Änderungen`} übernehmen`
                                : `${angenommen} von ${items.length} übernehmen`}
                          </button>
                          <button
                            onClick={() => klappeAuf(k.id)}
                            disabled={sende}
                            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                          >
                            Abbrechen
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </Karte>
              );
            })
          )}
        </div>
      )}
    </LeitstandRahmen>
  );
}
